import _ from 'lodash'
import apollo from '@/graphql'

import CallQueueQuery from '@/graphql/device_issue/CallQueueQuery.gql'
import StoreIssueDetailsQuery from '@/graphql/device_issue/CallQueueStoreIssueDetailsQuery.gql'
import DeviceIssueStatusesQuery from '@/graphql/device_issue/DeviceIssueStatusesQuery.gql'
import DeviceIssueSubtypesQuery from '@/graphql/device_issue/DeviceIssueSubtypesQuery.gql'
import DeviceIssueTypesQuery from '@/graphql/device_issue/DeviceIssueTypesQuery.gql'
import DeviceIssuesQuery from '@/graphql/device_issue/DeviceIssuesQuery.gql'
import PaginatedDeviceIssuesQuery from '@/graphql/device_issue/PaginatedDeviceIssuesQuery.gql'
import IssueEscalationReasonsQuery from '@/graphql/device_issue/IssueEscalationReasonsQuery.gql'
import SaveStoreIssuesDetails from '@/graphql/device_issue/SaveStoreIssuesDetails.gql'

// initial state
const state = {
  callQueue: [],
  deviceIssueStatuses: [],
  deviceIssueSubtypes: [],
  deviceIssueTypes: [],
  deviceIssues: [],
  storeIssueDetails: {},
  paginatedDeviceIssues: [],
  isLoadingCallQueue: false,
  isLoadingStoreIssueDetails: false,
  issueEscalationReasons: [],
}

// getters
const getters = {
  callQueue: st => st.callQueue,
  deviceIssueStatuses: st => st.deviceIssueStatuses,
  deviceIssueSubtypes: st => st.deviceIssueSubtypes,
  deviceIssueTypes: st => st.deviceIssueTypes,
  deviceIssues: st => st.deviceIssues,
  storeIssueDetails: st => st.storeIssueDetails,
  paginatedDeviceIssues: st => st.paginatedDeviceIssues,
  isLoadingCallQueue: st => st.isLoadingCallQueue,
  isLoadingStoreIssueDetails: st => st.isLoadingStoreIssueDetails,
  issueEscalationReasons: st => st.issueEscalationReasons,
}

// mutations
const mutations = {
  setDeviceIssues(st, deviceIssues) {
    state.deviceIssues = deviceIssues
  },

  setPaginatedDeviceIssues(st, paginatedDeviceIssues) {
    state.paginatedDeviceIssues = paginatedDeviceIssues
  },

  setDeviceIssueTypes(st, deviceIssueTypes) {
    state.deviceIssueTypes = deviceIssueTypes
  },

  setDeviceIssueSubtypes(st, deviceIssueSubtypes) {
    state.deviceIssueSubtypes = deviceIssueSubtypes
  },

  setDeviceIssueStatuses(st, deviceIssueStatuses) {
    state.deviceIssueStatuses = deviceIssueStatuses
  },

  setCallQueue(st, callQueue) {
    state.callQueue = callQueue
  },

  setIsLoadingCallQueue(state, isLoadingCallQueue) {
    state.isLoadingCallQueue = isLoadingCallQueue
  },

  setStoreIssueDetails(st, storeIssue) {
    const groupedIssues = _.groupBy(storeIssue, 'storeId')
    state.storeIssueDetails = { ...st.storeIssueDetails, ...groupedIssues }
  },

  setIsLoadingStoreIssueDetails(state, isLoadingStoreIssueDetails) {
    state.isLoadingStoreIssueDetails = isLoadingStoreIssueDetails
  },

  setIssueEscalationReasons(st, issueEscalationReasons) {
    state.issueEscalationReasons = issueEscalationReasons
  },
}

// actions
const actions = {
  loadPaginatedDeviceIssues({ commit }, { variables }) {
    apollo.defaultClient
      .watchQuery({
        query: PaginatedDeviceIssuesQuery,
        variables,
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data: { paginatedDeviceIssues } }) => {
        commit('setPaginatedDeviceIssues', paginatedDeviceIssues)
      })
  },

  loadDeviceIssues({ commit }) {
    apollo.defaultClient
      .watchQuery({
        query: DeviceIssuesQuery,
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data: { deviceIssues } }) => {
        commit('setDeviceIssues', deviceIssues)
      })
  },

  loadDeviceIssueTypes({ commit }) {
    apollo.defaultClient
      .watchQuery({
        query: DeviceIssueTypesQuery,
      })
      .subscribe(({ data: { deviceIssueTypes } }) => {
        commit('setDeviceIssueTypes', deviceIssueTypes)
      })
  },

  loadDeviceIssueSubtypes({ commit }) {
    apollo.defaultClient
      .watchQuery({
        query: DeviceIssueSubtypesQuery,
      })
      .subscribe(({ data: { deviceIssueSubtypes } }) => {
        commit('setDeviceIssueSubtypes', deviceIssueSubtypes)
      })
  },

  loadDeviceIssueStatuses({ commit }) {
    apollo.defaultClient
      .watchQuery({
        query: DeviceIssueStatusesQuery,
      })
      .subscribe(({ data: { deviceIssueStatuses } }) => {
        commit('setDeviceIssueStatuses', deviceIssueStatuses)
      })
  },

  loadCallQueue({ commit },
    {
      setLoading = true,
      paginationInfo,
      userFilter,
      dateFilter,
      storeClusterFilter,
    }) {
    if (setLoading) {
      commit('setIsLoadingCallQueue', true)
    }

    return new Promise((resolve) => {
      apollo.defaultClient
        .watchQuery({
          query: CallQueueQuery,
          variables: {
            pageSize: paginationInfo.pageSize,
            currentPage: paginationInfo.currentPage,
            userFilter,
            dateFilter,
            storeClusterFilter,
          },
          fetchPolicy: 'network-only',
        })
        .subscribe(({ data: { callQueue } }) => {
          commit('setCallQueue', callQueue)
          commit('setIsLoadingCallQueue', false)
          resolve()
        })
    })
  },

  loadStoreIssues({ commit }, { variables }) {
    commit('setIsLoadingStoreIssueDetails', true)
    return new Promise((resolve) => {
      apollo.defaultClient
        .query({
          query: StoreIssueDetailsQuery,
          variables,
          fetchPolicy: 'network-only',
        })
        .then(({ data: { callQueueStoreIssueDetails } }) => {
          commit('setStoreIssueDetails', callQueueStoreIssueDetails)
          commit('setIsLoadingStoreIssueDetails', false)
          resolve()
        })
    })
  },

  loadIssueEscalationReasons({ commit }) {
    apollo.defaultClient
      .watchQuery({
        query: IssueEscalationReasonsQuery,
      })
      .subscribe(({ data: { issueEscalationReason } }) => {
        commit('setIssueEscalationReasons', issueEscalationReason)
      })
  },

  saveStoreIssuesDetails(
    { rootGetters: { storeOpenIssues, storeClosedIssues, storeDetails } },
    { $message },
  ) {
    const issues = [...storeOpenIssues, ...storeClosedIssues]
    const fields = {
      storeId: storeDetails.id,
      issues: issues.map(i => ({
        id: i.id,
        subtypesId: i.subtypeId,
        statusId: i.status.id,
      })),
    }
    apollo.defaultClient
      .mutate({
        mutation: SaveStoreIssuesDetails,
        variables: { fields },
      })
      .catch((err) => {
        $message({
          message: err,
          type: 'error',
        })
      })
  },

}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/device_issue/DeviceIssueList.js